/**
 * Device-GNSS source — the phone's (or laptop's) real GPS, plugged into the same
 * seam as the simulator.
 *
 * It implements the same PositionSource interface and emits the same NMEA
 * (RMC + GGA + GST), so GnssIngest and everything downstream are untouched: the
 * app still only sees parsed fixes off the wire. The browser Geolocation API
 * gives us latitude/longitude, accuracy, and (when the device supplies them)
 * speed and heading. Where speed/heading come back null — stationary, or a
 * device that doesn't report them — we derive course and speed from successive
 * positions, and where we can't get a trustworthy course we emit zero speed so
 * no ground-track vector is drawn off a guess.
 *
 * Geolocation only works in a secure context (HTTPS or localhost). On a plain
 * http:// LAN/Tailscale IP the browser blocks it, so we detect that up front and
 * report it clearly rather than failing silently.
 */
import { rhumbBearingDeg, rhumbDistanceNm } from '@/nav';
import { encodeGGA, encodeGST, encodeRMC } from './nmea';
import type { PositionSource } from './source';

export type DeviceGnssStatus =
  | 'idle'
  | 'requesting'
  /**
   * Showing the position the phone was already holding, still waiting on a fix
   * of our own. Deliberately NOT 'live': it gets the boat on the chart in the
   * first second, but it could be from the car park an hour ago, so nothing
   * downstream should treat it as a fix. See fixReliable.ts.
   */
  | 'cached'
  | 'live'
  | 'nofix'
  | 'denied'
  | 'insecure'
  | 'unsupported'
  | 'error';

/**
 * How old a cached position may be and still be worth showing as a starting
 * point. Ten minutes: old enough to cover a phone that has been in a pocket
 * since the pontoon, short enough that it is still the same trip.
 */
const SEED_MAX_AGE_MS = 10 * 60 * 1000;

/** The cache lookup should be instant. If it is not, the watch will do the job. */
const SEED_TIMEOUT_MS = 4000;

export interface DeviceGnssOptions {
  /** Status callback so the UI can show acquiring / live / blocked. */
  onStatus?: (status: DeviceGnssStatus, message?: string) => void;
  /**
   * Called immediately after the phone's cached starting position is emitted,
   * with the time that position was ACTUALLY taken.
   *
   * It matters because the fix goes down the same NMEA pipe as a live one and is
   * therefore stamped with the moment it arrived, which makes an hour-old
   * position look brand new. The feed uses this to record its real age, so the
   * ordinary staleness machinery treats it exactly as what it is: a fix from
   * whenever the phone last had one.
   */
  onCachedPosition?: (fixTimeMs: number) => void;
  /** Injectable for tests; defaults to navigator.geolocation. */
  geolocation?: Geolocation;
  /** Injectable for tests; defaults to window.isSecureContext. */
  isSecureContext?: boolean;
}

/** One position sample, normalised from a GeolocationPosition (or a test). */
export interface DeviceFixSample {
  lat: number;
  lon: number;
  /** Horizontal accuracy, metres (the 68% radius the browser reports). */
  accuracyM: number;
  /** Ground speed, m/s, or null if the device doesn't supply it. */
  speedMs: number | null;
  /** Course over ground, °T, or null if the device doesn't supply it. */
  headingDeg: number | null;
  /** Fix time, ms since epoch. */
  timestampMs: number;
}

const MS_TO_KN = 1.943844;

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Map one device fix to NMEA lines (GGA, GST, RMC), deriving course and speed
 * from the previous accepted position when the device leaves them out. Pure and
 * exported so the mapping is unit-tested without a browser.
 */
export function deviceFixToSentences(
  s: DeviceFixSample,
  prev: { lon: number; lat: number; t: number } | null,
): string[] {
  const position: [number, number] = [s.lon, s.lat];
  const utc = new Date(s.timestampMs);

  // Start from whatever the device gave us.
  let sogKn: number | null =
    s.speedMs != null && Number.isFinite(s.speedMs) && s.speedMs >= 0 ? s.speedMs * MS_TO_KN : null;
  let cogDeg: number | null =
    s.headingDeg != null && Number.isFinite(s.headingDeg) ? s.headingDeg : null;

  // Fill the gaps from successive positions. Only trust a derived course when
  // the step is bigger than the accuracy circle and a real speed, so GPS jitter
  // at rest doesn't spin a fake heading.
  if ((sogKn == null || cogDeg == null) && prev) {
    const dtSec = (s.timestampMs - prev.t) / 1000;
    if (dtSec > 0) {
      const distNm = rhumbDistanceNm([prev.lon, prev.lat], position);
      const movedM = distNm * 1852;
      const derivedSog = (distNm / dtSec) * 3600; // nm per hour = knots
      const meaningful = movedM > Math.max(s.accuracyM, 2) && derivedSog > 0.3;
      if (sogKn == null) sogKn = meaningful ? derivedSog : 0;
      if (cogDeg == null) cogDeg = meaningful ? rhumbBearingDeg([prev.lon, prev.lat], position) : null;
    }
  }

  // Emit values: never NaN. With no usable course we send zero speed, which the
  // ground-track vector suppresses (it only draws at SOG > 0), so nothing is
  // drawn off a null.
  const emitCog = cogDeg ?? 0;
  const emitSog = cogDeg == null ? 0 : (sogKn ?? 0);

  // Accuracy: split equally across lat/lon so the ingest's RSS equals the
  // browser's reported metres, feeding the same accuracy ring and alarm.
  const acc = Number.isFinite(s.accuracyM) && s.accuracyM > 0 ? s.accuracyM : 10;
  const sigma = acc / Math.SQRT2;
  const hdop = clamp(acc / 4, 0.5, 9.9);

  // No satellites-used count: the browser/plugin position feed doesn't report
  // one, so we leave the GGA sat-count field empty (valid NMEA) rather than
  // synthesising a number. A fabricated count would read as real downstream.
  return [
    encodeGGA({ position, utc, fixQuality: 1, hdop, talker: 'GN' }),
    encodeGST({
      utc,
      rmsM: acc * 0.7,
      semiMajorM: acc,
      semiMinorM: acc * 0.8,
      orientDeg: 0,
      latErrM: sigma,
      lonErrM: sigma,
      altErrM: acc * 1.5,
      talker: 'GN',
    }),
    encodeRMC({ position, sogKn: emitSog, cogDeg: emitCog, utc, talker: 'GN' }),
  ];
}

export class DeviceGnssSource implements PositionSource {
  readonly kind = 'device';

  private listeners = new Set<(line: string) => void>();
  private watchId: number | null = null;
  private prev: { lon: number; lat: number; t: number } | null = null;
  private readonly onStatus?: (status: DeviceGnssStatus, message?: string) => void;
  private readonly onCachedPosition?: (fixTimeMs: number) => void;
  private readonly geo?: Geolocation;
  private readonly secure: boolean;

  constructor(opts: DeviceGnssOptions = {}) {
    this.onStatus = opts.onStatus;
    this.onCachedPosition = opts.onCachedPosition;
    this.geo =
      opts.geolocation ?? (typeof navigator !== 'undefined' ? navigator.geolocation : undefined);
    this.secure =
      opts.isSecureContext ?? (typeof window !== 'undefined' ? window.isSecureContext : true);
  }

  start(): void {
    if (this.watchId !== null) return;
    if (!this.geo) {
      this.onStatus?.('unsupported', 'This browser has no Geolocation API.');
      return;
    }
    if (!this.secure) {
      // The browser will block geolocation on plain http://; say so plainly
      // rather than letting it fail with a confusing permission error.
      this.onStatus?.(
        'insecure',
        'Phone GNSS needs a secure (HTTPS) connection. Open the app over an HTTPS URL — see the Tailscale Serve notes.',
      );
      return;
    }
    this.onStatus?.('requesting', 'Asking for location permission…');
    this.watchId = this.geo.watchPosition(
      (p) => {
        const c = p.coords;
        const sample: DeviceFixSample = {
          lat: c.latitude,
          lon: c.longitude,
          accuracyM: c.accuracy,
          speedMs: c.speed,
          headingDeg: c.heading,
          timestampMs: p.timestamp,
        };
        for (const line of deviceFixToSentences(sample, this.prev)) this.emit(line);
        this.prev = { lon: c.longitude, lat: c.latitude, t: p.timestamp };
        this.onStatus?.('live');
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          this.onStatus?.(
            'denied',
            this.secure
              ? 'Location permission denied. Allow location for this site and try again.'
              : 'Blocked: phone GNSS needs HTTPS.',
          );
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          this.onStatus?.('nofix', 'No fix yet — waiting for satellites.');
        } else if (err.code === err.TIMEOUT) {
          this.onStatus?.('nofix', 'Still waiting for a fix…');
        } else {
          this.onStatus?.('error', err.message || 'Geolocation error.');
        }
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 },
    );
    this.seedFromCache();
  }

  /**
   * Take the position the phone already has, so the boat is on the chart at once.
   *
   * The watch above asks for maximumAge 1000, which is right for a live feed and
   * wrong for the first second of the app's life: it refuses anything older than
   * a second, so we sit on "finding position" while the operating system holds a
   * perfectly good recent location it would hand over immediately. That wait was
   * being felt on every start, and it got worse once a renderer death started
   * ending the process, because then there is no background feed to inherit from
   * either.
   *
   * So we ask once, separately, for whatever is in the cache. High accuracy off,
   * because we are asking for something already recorded, not commissioning a new
   * fix.
   *
   * Three things this deliberately does NOT do. It does not report 'live', so
   * nothing downstream mistakes it for a fix (the boat draws red until a real one
   * lands). It does not set `prev`, because deriving a course between an hour-old
   * cached position and the first real fix would invent a speed of some hundreds
   * of knots. And it does nothing at all if a real fix has already arrived, which
   * on a warm receiver it often will have.
   */
  private seedFromCache(): void {
    if (!this.geo || !this.secure) return;
    this.geo.getCurrentPosition(
      (p) => {
        if (this.watchId === null) return; // stopped while we were asking
        if (this.prev !== null) return; // a real fix beat us to it
        const c = p.coords;
        const sample: DeviceFixSample = {
          lat: c.latitude,
          lon: c.longitude,
          accuracyM: c.accuracy,
          speedMs: null,
          headingDeg: null,
          timestampMs: p.timestamp,
        };
        for (const line of deviceFixToSentences(sample, null)) this.emit(line);
        // emit runs the ingest listeners synchronously, so by here the fix is
        // committed and stamped with now. Correct it to when the position was
        // really taken, so it ages honestly from this moment on: a cached fix
        // from a minute ago is a minute old, and one from the car park an hour
        // ago is an hour old and the boat goes red. Guarded against a clock that
        // reports the future, which would age backwards.
        if (p.timestamp > 0 && p.timestamp < Date.now()) this.onCachedPosition?.(p.timestamp);
        this.onStatus?.('cached', 'Last known position, still looking for a fix.');
      },
      () => {
        // Nothing cached, or it was refused. The watch is still running and this
        // was only ever a head start, so there is nothing to report.
      },
      { enableHighAccuracy: false, maximumAge: SEED_MAX_AGE_MS, timeout: SEED_TIMEOUT_MS },
    );
  }

  stop(): void {
    if (this.watchId !== null && this.geo) this.geo.clearWatch(this.watchId);
    this.watchId = null;
    this.prev = null;
    this.onStatus?.('idle');
  }

  onSentence(cb: (line: string) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private emit(line: string): void {
    for (const cb of this.listeners) cb(line);
  }
}
